import React, { useState } from 'react';
import { CxPortalLight, PronetxLogoLight } from '@@/icons';

import { Alert, AlertDescription, Button, Input } from '@@/ui/primitives';
import { AlertTriangle } from 'lucide-react';
import { useLoginBannerAndAvailability } from './hooks/useLoginBannerAndAvailability';
import { LoginPageBanner } from '../components/LoginPageBanner';

interface CompanyIdInterstitialProps {
  onSubmit: (companyId: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export default function CompanyIdInterstitial({ onSubmit, isLoading = false, error }: CompanyIdInterstitialProps) {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState('');
  const { banner, hideLogin, loading } = useLoginBannerAndAvailability();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setValidationError('Company ID is required');
      return;
    }

    setValidationError('');
    await onSubmit(email.trim());
  };

  const errorMessage = () => {
    const displayError = error || validationError;
    if (!displayError) return null;

    return (
      <Alert variant='destructive' className='mt-2'>
        <AlertTriangle className='h-4 w-4' />
        <AlertDescription>{displayError}</AlertDescription>
      </Alert>
    );
  };

  return (
    <div className='admin-login-page-container' role='main' aria-label='Company ID'>
      <div
        className='admin-login-page-container-content'
        style={{
          backgroundImage: 'url(/images/login-page-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <PronetxLogoLight className='PronetxLogo-logo' aria-label='Pronetx Logo' />

        <div className='form-container' role='form' aria-label='email form'>
          <div className='form-container-header' role='banner'>
            <CxPortalLight aria-label='Pronetx' className='text-white' />
          </div>

          {/* Banner identical to login page styling */}
          {!loading && banner && <LoginPageBanner banner={banner} />}

          {!loading && hideLogin === true ? (
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px', textAlign: 'center', color: 'white' }}>
              Service temporarily unavailable. Please try again later.
            </div>
          ) : (
            <div className='form-container-content'>
              <div role='region' aria-label='email setup form'>
                <form onSubmit={handleSubmit} aria-label='email setup form'>
                  <div className='text-center mb-6'>
                    <h2 className='text-lg font-semibold text-[#1E1E1E] mb-2'>Welcome to CX Portal</h2>
                    <p className='text-sm text-[#404D5C] m-0'>Please enter your email to continue</p>
                  </div>

                  <div className='flex flex-col  max-w-lg mx-auto'>
                    <div className='flex flex-col gap-2'>
                      {/* <Label htmlFor='companyId' className="font-medium text-[#404D5C]">
                      Company ID
                    </Label> */}
                      <Input
                        id='email'
                        name='email'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        // className={`login-form-container-input font-medium text-sm p-3 rounded-[9px] ${validationError ? 'border-[#FF4B4B]' : 'border-[#b3bbbb]'}`}
                        required
                        size={20}
                        className='py-6'
                        autoComplete='organization'
                        aria-label='Enter your email'
                        aria-invalid={!!validationError}
                        aria-describedby={validationError ? 'email-error' : undefined}
                        placeholder='your-email@company.com'
                        disabled={isLoading}
                      />
                    </div>
                    {validationError && (
                      <div className='text-red-500 text-xs' id='email-error'>
                        {validationError}
                      </div>
                    )}
                    {errorMessage()}
                  </div>
                  <div className='flex justify-center mt-6'>
                    <Button
                      type='submit'
                      size='lg'
                      style={{ backgroundColor: '#1E1E1E' }}
                      aria-label='Continue with email'
                      disabled={isLoading}
                    >
                      {isLoading ? 'Setting up...' : 'Continue'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
